import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Rocket, Heart, Volume2, VolumeX, Trophy, Shield, Zap } from "lucide-react";
import { API } from "./upload";

const LIVES = 8;
const BOUNDS = { x: 16, y: 9 };

// ---------- tiny WebAudio sfx ----------
const makeSfx = () => {
  let ctx = null;
  const ensure = () => (ctx = ctx || new (window.AudioContext || window.webkitAudioContext)());
  const blip = (f0, f1, dur, type = "square", vol = 0.06) => {
    try {
      const c = ensure();
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = type;
      o.frequency.setValueAtTime(f0, c.currentTime);
      o.frequency.exponentialRampToValueAtTime(f1, c.currentTime + dur);
      g.gain.setValueAtTime(vol, c.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + dur);
      o.connect(g).connect(c.destination);
      o.start();
      o.stop(c.currentTime + dur);
    } catch { /* audio unavailable */ }
  };
  return {
    shoot: () => blip(880, 160, 0.12, "square", 0.045),
    enemyShoot: () => blip(320, 90, 0.15, "sawtooth", 0.03),
    boom: () => blip(140, 30, 0.4, "sawtooth", 0.09),
    hit: () => blip(70, 40, 0.5, "triangle", 0.1),
    pickup: () => blip(520, 1240, 0.22, "sine", 0.07),
    shieldPop: () => blip(980, 240, 0.25, "sine", 0.08),
  };
};

// Leaderboard panel — per-user best scores from the backend.
const Leaderboard = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/funtime/starwar/leaderboard`).then((r) => setData(r.data)).catch(() => {});
  }, [refreshKey]);
  if (!data || !data.top?.length) return null;
  return (
    <div className="rounded-xl border border-border/60 bg-card/60 p-4" data-testid="starwar-leaderboard">
      <p className="font-mono text-xs font-bold text-primary flex items-center gap-1.5 mb-2">
        <Trophy className="w-3.5 h-3.5 text-amber-400" /> Top Pilots
        {data.me?.rank && <span className="ml-auto text-[10px] text-muted-foreground" data-testid="starwar-lb-me">Your rank: #{data.me.rank} · best {data.me.best_score.toLocaleString()}</span>}
      </p>
      <ol className="space-y-1">
        {data.top.map((r, i) => (
          <li key={i} className={`flex items-center gap-2 rounded px-2 py-1 font-mono text-xs ${r.is_me ? "bg-primary/15 text-primary font-bold" : "text-card-foreground"}`} data-testid={`starwar-lb-row-${i}`}>
            <span className="w-6 text-muted-foreground">#{i + 1}</span>
            <span className="truncate flex-1">{r.name}</span>
            <span className="text-[10px] text-muted-foreground">{r.games} game{r.games === 1 ? "" : "s"}</span>
            <span className="font-bold text-amber-300 w-20 text-right">{r.best_score.toLocaleString()}</span>
          </li>
        ))}
      </ol>
    </div>
  );
};

// ---------- ship builders (original low-poly, star-wars-flavoured) ----------
const buildPlayerShip = () => {
  const ship = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.ConeGeometry(0.55, 3.2, 8),
    new THREE.MeshStandardMaterial({ color: 0xd8d8e0, metalness: 0.6, roughness: 0.35 })
  );
  body.rotation.x = -Math.PI / 2;
  ship.add(body);
  const wingMat = new THREE.MeshStandardMaterial({ color: 0xb8b8c4, metalness: 0.5, roughness: 0.4 });
  const tipMat = new THREE.MeshStandardMaterial({ color: 0xd23b2f, emissive: 0x501008 });
  [[-1, 1], [1, 1], [-1, -1], [1, -1]].forEach(([sx, sy]) => {
    const wing = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.08, 1.0), wingMat);
    wing.position.set(sx * 1.35, sy * 0.42, 0.5);
    wing.rotation.z = sx * sy * 0.28;
    ship.add(wing);
    const tip = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 0.8, 6), tipMat);
    tip.rotation.x = Math.PI / 2;
    tip.position.set(sx * 2.3, sy * 0.75, 0.35);
    ship.add(tip);
  });
  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(0.22, 8, 8),
    new THREE.MeshBasicMaterial({ color: 0x66aaff, transparent: true, opacity: 0.9 })
  );
  glow.position.set(0, 0, 1.7);
  ship.add(glow);
  return ship;
};

const ENEMY_KINDS = {
  tie: { ring: 0xff5648, scale: 1.45 },
  dart: { ring: 0x35e0ff, scale: 1.3 },
  orb: { ring: 0xff45d8, scale: 1.75 },
};

const buildEnemyShip = (kind = "tie") => {
  const e = new THREE.Group();
  if (kind === "dart") {
    // swept-wing interceptor — fast, banks side to side
    const body = new THREE.Mesh(
      new THREE.ConeGeometry(0.5, 2.4, 8),
      new THREE.MeshStandardMaterial({ color: 0xb8c4dd, metalness: 0.65, roughness: 0.3, emissive: 0x27304a })
    );
    body.rotation.x = Math.PI / 2;
    e.add(body);
    const wingMat = new THREE.MeshStandardMaterial({ color: 0x6a7590, metalness: 0.5, roughness: 0.5, emissive: 0x141a28, side: THREE.DoubleSide });
    [-1, 1].forEach((s) => {
      const wing = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.08, 1.1), wingMat);
      wing.position.set(s * 0.95, 0, 0.35);
      wing.rotation.z = s * 0.28;
      e.add(wing);
    });
    const engine = new THREE.Mesh(new THREE.SphereGeometry(0.24, 8, 8), new THREE.MeshBasicMaterial({ color: 0xffa028 }));
    engine.position.z = -1.15;
    e.add(engine);
  } else if (kind === "orb") {
    // spiked heavy raider — big, slow, fires more often
    const ball = new THREE.Mesh(
      new THREE.SphereGeometry(0.95, 12, 12),
      new THREE.MeshStandardMaterial({ color: 0x7d6a92, metalness: 0.55, roughness: 0.4, emissive: 0x2c1a3a })
    );
    e.add(ball);
    const spikeMat = new THREE.MeshStandardMaterial({ color: 0xd8c6ff, metalness: 0.6, roughness: 0.3, emissive: 0x241536 });
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const spike = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.7, 6), spikeMat);
      spike.position.set(Math.cos(a) * 1.05, Math.sin(a) * 1.05, 0);
      spike.rotation.z = a - Math.PI / 2;
      e.add(spike);
    }
    const eyeO = new THREE.Mesh(new THREE.SphereGeometry(0.3, 8, 8), new THREE.MeshBasicMaterial({ color: 0xff45d8 }));
    eyeO.position.z = 0.85;
    e.add(eyeO);
  } else {
    // classic TIE-style fighter
    const ball = new THREE.Mesh(
      new THREE.SphereGeometry(0.6, 10, 10),
      new THREE.MeshStandardMaterial({ color: 0x9aa4b8, metalness: 0.6, roughness: 0.35, emissive: 0x232c42 })
    );
    e.add(ball);
    const panelMat = new THREE.MeshStandardMaterial({ color: 0x59647a, metalness: 0.4, roughness: 0.55, emissive: 0x0e1218, side: THREE.DoubleSide });
    [-1, 1].forEach((s) => {
      const panel = new THREE.Mesh(new THREE.CylinderGeometry(1.05, 1.05, 0.08, 6), panelMat);
      panel.rotation.z = Math.PI / 2;
      panel.position.x = s * 1.15;
      e.add(panel);
      const strut = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.6, 6), panelMat);
      strut.rotation.z = Math.PI / 2;
      strut.position.x = s * 0.85;
      e.add(strut);
    });
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.26, 8, 8), new THREE.MeshBasicMaterial({ color: 0x9dff55 }));
    eye.position.z = 0.55;
    e.add(eye);
  }
  // bright pulsing targeting ring so incoming ships pop against the starfield
  const marker = new THREE.Mesh(
    new THREE.TorusGeometry(kind === "orb" ? 2.1 : 1.7, 0.09, 8, 32),
    new THREE.MeshBasicMaterial({ color: ENEMY_KINDS[kind].ring, transparent: true, opacity: 0.95 })
  );
  e.add(marker);
  e.userData.marker = marker;
  return e;
};

const buildBossShip = () => {
  const g = new THREE.Group();
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(2.2, 14, 14),
    new THREE.MeshStandardMaterial({ color: 0x2a2f3a, metalness: 0.8, roughness: 0.35 })
  );
  g.add(core);
  const panelMat = new THREE.MeshStandardMaterial({ color: 0x14161c, metalness: 0.5, roughness: 0.6, side: THREE.DoubleSide });
  [-1, 1].forEach((s) => {
    const panel = new THREE.Mesh(new THREE.CylinderGeometry(3.4, 3.4, 0.18, 8), panelMat);
    panel.rotation.z = Math.PI / 2;
    panel.position.x = s * 4.2;
    g.add(panel);
    const strut = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 2.4, 8), panelMat);
    strut.rotation.z = Math.PI / 2;
    strut.position.x = s * 2.9;
    g.add(strut);
  });
  const eyeB = new THREE.Mesh(new THREE.SphereGeometry(0.6, 10, 10), new THREE.MeshBasicMaterial({ color: 0xff3b2e }));
  eyeB.position.z = 2.0;
  g.add(eyeB);
  const glowRing = new THREE.Mesh(new THREE.TorusGeometry(2.5, 0.12, 8, 40), new THREE.MeshBasicMaterial({ color: 0xff3b2e, transparent: true, opacity: 0.7 }));
  glowRing.position.z = 0.4;
  g.add(glowRing);
  return g;
};

export default function StarWarGame() {
  const mountRef = useRef(null);
  const stateRef = useRef({});
  const sfxRef = useRef(null);
  const [phase, setPhase] = useState("start"); // start | playing | over
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(LIVES);
  const [best, setBest] = useState(() => Number(localStorage.getItem("starwar_best") || 0));
  const [muted, setMuted] = useState(false);
  const [shieldCt, setShieldCt] = useState(0);
  const [rapidLeft, setRapidLeft] = useState(0);
  const [bossState, setBossState] = useState(null); // {hp, max}
  const [lbKey, setLbKey] = useState(0);
  const mutedRef = useRef(false);
  const phaseRef = useRef("start");
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || stateRef.current.renderer) return;
    sfxRef.current = makeSfx();
    const S = stateRef.current;

    // --- scene / camera / renderer ---
    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x02030a, 340, 850);
    const camera = new THREE.PerspectiveCamera(62, 1, 0.1, 1200);
    camera.position.set(0, 2.2, 14);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x02030a);
    mount.appendChild(renderer.domElement);
    scene.add(new THREE.AmbientLight(0x8899bb, 0.9));
    const sun = new THREE.DirectionalLight(0xffeecc, 1.4);
    sun.position.set(30, 40, 20);
    scene.add(sun);

    // --- starfield ---
    const starGeo = new THREE.BufferGeometry();
    const starCount = 1600;
    const pos = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 400;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 240;
      pos[i * 3 + 2] = -Math.random() * 700 + 20;
    }
    starGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    const stars = new THREE.Points(starGeo, new THREE.PointsMaterial({ color: 0xffffff, size: 0.7, sizeAttenuation: true, transparent: true, opacity: 0.9 }));
    scene.add(stars);

    // --- distant planets for depth ---
    const planet1 = new THREE.Mesh(
      new THREE.SphereGeometry(55, 32, 32),
      new THREE.MeshStandardMaterial({ color: 0xc97b3a, roughness: 0.9, emissive: 0x2a1206 })
    );
    planet1.position.set(-160, 60, -640);
    scene.add(planet1);
    const planet2 = new THREE.Mesh(
      new THREE.SphereGeometry(34, 32, 32),
      new THREE.MeshStandardMaterial({ color: 0x5a7fae, roughness: 0.8, emissive: 0x0a1424 })
    );
    planet2.position.set(190, -40, -560);
    scene.add(planet2);
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(52, 6, 12, 60),
      new THREE.MeshStandardMaterial({ color: 0x9a8f78, roughness: 0.9 })
    );
    ring.position.copy(planet2.position);
    ring.rotation.x = Math.PI / 2.4;
    scene.add(ring);

    // --- drifting asteroids ---
    const asteroidMat = new THREE.MeshStandardMaterial({ color: 0x6f6a62, roughness: 1 });
    S.asteroids = [];
    for (let i = 0; i < 12; i++) {
      const a = new THREE.Mesh(new THREE.DodecahedronGeometry(1.5 + Math.random() * 3, 0), asteroidMat);
      a.position.set((Math.random() - 0.5) * 140, (Math.random() - 0.5) * 70, -120 - Math.random() * 380);
      a.userData.spin = new THREE.Vector3(Math.random(), Math.random(), Math.random()).multiplyScalar(0.6);
      S.asteroids.push(a);
      scene.add(a);
    }

    // --- player ---
    const player = buildPlayerShip();
    scene.add(player);
    const shieldBubble = new THREE.Mesh(
      new THREE.SphereGeometry(3.1, 20, 20),
      new THREE.MeshBasicMaterial({ color: 0x38d8ff, transparent: true, opacity: 0.16, depthWrite: false })
    );
    shieldBubble.visible = false;
    player.add(shieldBubble);

    Object.assign(S, {
      scene, camera, renderer, player, stars, planet1, planet2, ring, shieldBubble,
      enemies: [], playerBolts: [], enemyBolts: [], booms: [], pickups: [],
      mouse: { x: 0, y: 0 }, firing: false, lastShot: 0, spawnAt: 0,
      score: 0, lives: LIVES, running: false, t: 0, flash: 0,
      shieldCharges: 0, rapidUntil: 0, lastRapidShown: 0,
      boss: null, nextBossAt: 500, invulnUntil: 0,
    });

    const size = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    size();
    const ro = new ResizeObserver(size);
    ro.observe(mount);

    // --- input ---
    const onMove = (e) => {
      const r = renderer.domElement.getBoundingClientRect();
      S.mouse.x = ((e.clientX - r.left) / r.width) * 2 - 1;
      S.mouse.y = -(((e.clientY - r.top) / r.height) * 2 - 1);
    };
    const onDown = (e) => { if (e.button === 0) S.firing = true; };
    const onUp = () => { S.firing = false; };
    const onCtx = (e) => e.preventDefault();
    renderer.domElement.addEventListener("mousemove", onMove);
    renderer.domElement.addEventListener("mousedown", onDown);
    renderer.domElement.addEventListener("contextmenu", onCtx);
    window.addEventListener("mouseup", onUp);

    const boltGeo = new THREE.CylinderGeometry(0.09, 0.09, 4.4, 6);
    const redMat = new THREE.MeshBasicMaterial({ color: 0xff3524 });
    const greenMat = new THREE.MeshBasicMaterial({ color: 0x59ff3a });
    const purpleMat = new THREE.MeshBasicMaterial({ color: 0xc44dff });

    const spawnBolt = (from, dir, mat, speed, list) => {
      const b = new THREE.Mesh(boltGeo, mat);
      b.position.copy(from);
      b.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
      b.userData.vel = dir.clone().normalize().multiplyScalar(speed);
      list.push(b);
      scene.add(b);
    };

    const explode = (at, color) => {
      const group = new THREE.Group();
      const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1 });
      for (let i = 0; i < 14; i++) {
        const p = new THREE.Mesh(new THREE.TetrahedronGeometry(0.22), mat.clone());
        p.position.copy(at);
        p.userData.vel = new THREE.Vector3((Math.random() - 0.5), (Math.random() - 0.5), (Math.random() - 0.5)).multiplyScalar(26);
        group.add(p);
      }
      group.userData.life = 0.65;
      S.booms.push(group);
      scene.add(group);
    };

    const spawnEnemy = (near = false) => {
      const r = Math.random();
      const kind = r < 0.45 ? "tie" : r < 0.8 ? "dart" : "orb";
      const e = buildEnemyShip(kind);
      const marker = e.userData.marker;
      e.scale.setScalar(ENEMY_KINDS[kind].scale);
      const z = near ? -140 - Math.random() * 60 : -220 - Math.random() * 120;
      e.position.set((Math.random() - 0.5) * 2 * BOUNDS.x, (Math.random() - 0.5) * 2 * BOUNDS.y, z);
      const baseSpeed = 26 + Math.min(22, S.score / 60) + Math.random() * 8;
      e.userData = {
        kind, marker,
        speed: kind === "dart" ? baseSpeed * 1.35 : kind === "orb" ? baseSpeed * 0.75 : baseSpeed,
        weave: Math.random() * Math.PI * 2,
        weaveAmp: (kind === "dart" ? 2.6 : 1.5) + Math.random() * 2.5,
        nextFire: S.t + (kind === "orb" ? 0.9 : 1.2) + Math.random() * 2,
        fireGap: kind === "orb" ? 1.3 : 1.8,
        spin: kind === "tie" ? 0.8 : kind === "orb" ? 1.6 : 0,
        hitR2: kind === "orb" ? 11 : kind === "dart" ? 6.6 : 7.3,
        hitPlayerD: kind === "orb" ? 4 : 3.2,
        baseX: e.position.x, baseY: e.position.y,
      };
      S.enemies.push(e);
      scene.add(e);
    };

    const removeFrom = (list, obj) => {
      const i = list.indexOf(obj);
      if (i >= 0) list.splice(i, 1);
      scene.remove(obj);
    };

    // --- power-ups dropped by destroyed enemies ---
    const spawnPickup = (at) => {
      const type = Math.random() < 0.5 ? "shield" : "rapid";
      const mesh = new THREE.Mesh(
        type === "shield" ? new THREE.OctahedronGeometry(0.75) : new THREE.TetrahedronGeometry(0.85),
        new THREE.MeshBasicMaterial({ color: type === "shield" ? 0x38d8ff : 0xffb02e })
      );
      mesh.position.copy(at);
      mesh.userData = { type, baseY: at.y };
      S.pickups.push(mesh);
      scene.add(mesh);
    };

    const collectPickup = (p) => {
      if (p.userData.type === "shield") {
        S.shieldCharges = Math.min(3, S.shieldCharges + 1);
        setShieldCt(S.shieldCharges);
      } else {
        S.rapidUntil = S.t + 8;
      }
      if (!mutedRef.current) sfxRef.current.pickup();
      removeFrom(S.pickups, p);
    };

    const playerHit = () => {
      if (S.t < S.invulnUntil) return; // brief grace period after each hit
      if (S.shieldCharges > 0) {
        S.shieldCharges -= 1;
        setShieldCt(S.shieldCharges);
        shieldBubble.material.opacity = 0.5; // absorb pulse, decays in loop
        S.invulnUntil = S.t + 0.8;
        if (!mutedRef.current) sfxRef.current.shieldPop();
        return;
      }
      S.lives -= 1;
      S.flash = 1;
      S.invulnUntil = S.t + 1.6;
      if (!mutedRef.current) sfxRef.current.hit();
      setLives(S.lives);
      if (S.lives <= 0) endGame();
    };

    const endGame = () => {
      S.running = false;
      setPhase("over");
      setBest((b) => {
        const nb = Math.max(b, S.score);
        localStorage.setItem("starwar_best", String(nb));
        return nb;
      });
      // per-user leaderboard
      axios.post(`${API}/funtime/starwar/score`, { score: S.score })
        .then(() => setLbKey((k) => k + 1))
        .catch(() => {});
    };

    S.reset = () => {
      [...S.enemies, ...S.playerBolts, ...S.enemyBolts, ...S.booms, ...S.pickups].forEach((o) => scene.remove(o));
      S.enemies = []; S.playerBolts = []; S.enemyBolts = []; S.booms = []; S.pickups = [];
      S.score = 0; S.lives = LIVES; S.t = 0; S.spawnAt = 0.8; S.flash = 0;
      S.lastShot = 0; // CRITICAL: t restarts at 0 — a stale lastShot blocked shooting after restart
      S.shieldCharges = 0; S.rapidUntil = 0; S.lastRapidShown = 0;
      if (S.boss) scene.remove(S.boss);
      S.boss = null; S.nextBossAt = 500; S.invulnUntil = 0;
      player.position.set(0, 0, 0);
      player.visible = true;
      setScore(0);
      setLives(LIVES);
      setShieldCt(0);
      setRapidLeft(0);
      setBossState(null);
      S.running = true;
      spawnEnemy(true);  // oncoming ships right from the start
      spawnEnemy(true);
      spawnEnemy(false);
    };

    // --- main loop ---
    window.__SW_TEST = {
      addScore: (n) => { S.score += n; setScore(S.score); },
      setNextBoss: (n) => { S.nextBossAt = n * 500; },
      state: () => ({
        score: S.score, running: S.running, bolts: S.playerBolts.length, enemyBolts: S.enemyBolts.length,
        kinds: S.enemies.map((e) => e.userData.kind),
        boss: S.boss ? { num: S.boss.userData.num, volley: S.boss.userData.volley || 0 } : null,
      }),
    };
    const clock = new THREE.Clock();
    const animate = () => {
      S.raf = requestAnimationFrame(animate);
      const dt = Math.min(0.05, clock.getDelta());
      S.t += dt;

      // starfield flight + scenery
      const sp = starGeo.attributes.position.array;
      const starSpeed = S.running ? 60 : 14;
      for (let i = 0; i < starCount; i++) {
        sp[i * 3 + 2] += starSpeed * dt;
        if (sp[i * 3 + 2] > 22) sp[i * 3 + 2] = -700;
      }
      starGeo.attributes.position.needsUpdate = true;
      planet1.rotation.y += dt * 0.02;
      planet2.rotation.y += dt * 0.03;
      S.asteroids.forEach((a) => {
        a.rotation.x += a.userData.spin.x * dt;
        a.rotation.y += a.userData.spin.y * dt;
        a.position.z += (S.running ? 9 : 3) * dt;
        if (a.position.z > 30) a.position.z = -480;
      });

      // player steering (mouse)
      const tx = S.mouse.x * BOUNDS.x;
      const ty = S.mouse.y * BOUNDS.y;
      const px = player.position.x;
      player.position.x += (tx - player.position.x) * Math.min(1, dt * 7);
      player.position.y += (ty - player.position.y) * Math.min(1, dt * 7);
      player.rotation.z = THREE.MathUtils.clamp((px - tx) * 0.09, -0.7, 0.7);
      player.rotation.x = THREE.MathUtils.clamp((player.position.y - ty) * 0.05, -0.4, 0.4);
      camera.position.x = player.position.x * 0.25;
      camera.position.y = 2.2 + player.position.y * 0.2;
      camera.lookAt(player.position.x * 0.6, player.position.y * 0.6, -60);

      if (S.running) {
        // shooting — upgrades every 100 pts, BIG power jump every 500 pts (power caps at 1500)
        const rapid = S.t < S.rapidUntil;
        const cappedScore = Math.min(S.score, 1500);
        const level = 1 + Math.floor(cappedScore / 100);
        const tier = Math.floor(cappedScore / 500);
        let cd = rapid ? 0.05 : Math.max(0.05, 0.14 - (level - 1) * 0.012);
        cd *= Math.max(0.6, 1 - tier * 0.08);
        const boltSpeed = Math.min(260, 190 + tier * 15);
        if (S.firing && S.t - S.lastShot > cd) {
          S.lastShot = S.t;
          let points;
          if (level >= 4 || (rapid && level >= 2)) points = [[-2.3, 0.7], [2.3, 0.7], [-2.3, -0.75], [2.3, -0.75]];
          else if (level >= 2 || rapid) points = [[-2.3, 0.7], [2.3, 0.7]];
          else points = [[(S.shotSide = !S.shotSide) ? 2.3 : -2.3, 0.7]];
          points.forEach(([ox, oy]) => {
            const from = player.position.clone().add(new THREE.Vector3(ox, oy, -0.5));
            spawnBolt(from, new THREE.Vector3(0, 0, -1), redMat, boltSpeed, S.playerBolts);
          });
          if (tier >= 1) {
            // angled nose spread — widens with every 500 points
            const spread = Math.min(0.16, 0.06 + (tier - 1) * 0.02);
            [spread, -spread].forEach((sx) => {
              const from = player.position.clone().add(new THREE.Vector3(0, 0.2, -1));
              spawnBolt(from, new THREE.Vector3(sx, 0, -1).normalize(), redMat, boltSpeed, S.playerBolts);
            });
          }
          if (!mutedRef.current) sfxRef.current.shoot();
        }
        // invulnerability blink after a hit
        player.visible = S.t >= S.invulnUntil || Math.floor(S.t * 12) % 2 === 0;
        // rapid HUD countdown (update at most once per second)
        const remShown = rapid ? Math.ceil(S.rapidUntil - S.t) : 0;
        if (remShown !== S.lastRapidShown) {
          S.lastRapidShown = remShown;
          setRapidLeft(remShown);
        }
        // shield bubble visual
        shieldBubble.visible = S.shieldCharges > 0;
        if (shieldBubble.material.opacity > 0.16) shieldBubble.material.opacity -= dt * 0.8;

        // spawn enemies (ramps up; slower while a boss is on screen)
        if (S.t > S.spawnAt) {
          spawnEnemy();
          let interval = Math.max(0.55, 1.7 - S.score / 900 - S.t / 120);
          if (S.boss) interval *= 1.7;
          S.spawnAt = S.t + interval;
        }

        // boss waves — every 500 points, +500 bonus on kill
        if (!S.boss && S.score >= S.nextBossAt) {
          const bossNum = Math.floor(S.nextBossAt / 500);
          const bs = buildBossShip();
          bs.position.set(0, 2, -300);
          bs.userData = { hp: 8 + bossNum * 2, max: 8 + bossNum * 2, nextFire: S.t + 1.2, num: bossNum };
          S.boss = bs;
          S.nextBossAt += 500;
          scene.add(bs);
          setBossState({ hp: bs.userData.hp, max: bs.userData.max, num: bossNum });
        }
        if (S.boss) {
          const bs = S.boss;
          const u = bs.userData;
          if (!u.arrived) {
            bs.position.z += 32 * dt;
            if (bs.position.z >= -62) u.arrived = true;
          } else if (u.num >= 3) {
            bs.position.z = -62 + Math.sin(S.t * 0.8) * 7; // later bosses lunge in and out
          }
          const agil = 1 + Math.min(1, (u.num - 1) * 0.25); // faster strafing every boss
          bs.position.x = Math.sin(S.t * 0.5 * agil) * (10 + Math.min(5, u.num));
          bs.position.y = 1 + Math.cos(S.t * 0.35 * agil) * 5;
          bs.rotation.z += dt * 0.25;
          if (S.t > u.nextFire) {
            u.volley = (u.volley || 0) + 1;
            u.nextFire = S.t + Math.max(0.7, 1.3 - u.num * 0.06);
            // attack patterns grow with each boss:
            //  0 twin aimed lasers · 1 aimed 3-bolt fan (boss 2+) · 2 radial ring burst (boss 3+, past 1500 pts)
            let mode = 0;
            if (u.num === 2) mode = u.volley % 3 === 0 ? 1 : 0;
            else if (u.num >= 3) mode = u.volley % 3;
            if (mode === 0) {
              [-1, 1].forEach((s) => {
                const from = bs.position.clone().add(new THREE.Vector3(s * 3.5, 0, 1));
                const dir = player.position.clone().sub(from).normalize();
                spawnBolt(from, dir, purpleMat, 72, S.enemyBolts);
              });
            } else if (mode === 1) {
              const from = bs.position.clone().add(new THREE.Vector3(0, 0, 1.5));
              const base = player.position.clone().sub(from).normalize();
              [-0.22, 0, 0.22].forEach((ox) => {
                spawnBolt(from, base.clone().add(new THREE.Vector3(ox, 0, 0)).normalize(), purpleMat, 66, S.enemyBolts);
              });
            } else {
              // rotating ring burst — dodge through the gaps
              for (let k = 0; k < 8; k++) {
                const a = (k / 8) * Math.PI * 2 + u.volley * 0.4;
                const from = bs.position.clone().add(new THREE.Vector3(0, 0, 1.5));
                spawnBolt(from, new THREE.Vector3(Math.cos(a) * 0.5, Math.sin(a) * 0.5, 1).normalize(), purpleMat, 55, S.enemyBolts);
              }
            }
            if (u.num >= 4 && u.volley % 4 === 0) { spawnEnemy(true); spawnEnemy(true); } // escort reinforcements
            if (!mutedRef.current) sfxRef.current.enemyShoot();
          }
        }

        // enemies
        for (const e of [...S.enemies]) {
          const u = e.userData;
          e.position.z += u.speed * dt;
          e.position.x = u.baseX + Math.sin(S.t * 1.4 + u.weave) * u.weaveAmp;
          e.position.y = u.baseY + Math.cos(S.t * 1.1 + u.weave) * u.weaveAmp * 0.6;
          if (u.kind === "dart") e.rotation.z = Math.sin(S.t * 2 + u.weave) * 0.45; // banking
          else e.rotation.z += dt * u.spin;
          if (u.marker) u.marker.material.opacity = 0.55 + 0.4 * Math.sin(S.t * 5 + u.weave);
          if (S.t > u.nextFire && e.position.z < -30) {
            u.nextFire = S.t + u.fireGap + Math.random() * 2;
            const dir = player.position.clone().sub(e.position).normalize();
            spawnBolt(e.position.clone(), dir, greenMat, 78, S.enemyBolts);
            if (!mutedRef.current) sfxRef.current.enemyShoot();
          }
          if (e.position.z > 6) {
            if (e.position.distanceTo(player.position) < u.hitPlayerD) playerHit();
            removeFrom(S.enemies, e);
          }
        }

        // player bolts vs boss + enemies
        for (const b of [...S.playerBolts]) {
          b.position.addScaledVector(b.userData.vel, dt);
          if (b.position.z < -750) { removeFrom(S.playerBolts, b); continue; }
          if (S.boss && b.position.distanceToSquared(S.boss.position) < 30) {
            explode(b.position.clone(), 0xff6a3a);
            removeFrom(S.playerBolts, b);
            S.boss.userData.hp -= 1;
            setBossState({ hp: S.boss.userData.hp, max: S.boss.userData.max, num: S.boss.userData.num });
            if (!mutedRef.current) sfxRef.current.boom();
            if (S.boss.userData.hp <= 0) {
              const at = S.boss.position.clone();
              explode(at, 0xffa02a);
              explode(at.clone().add(new THREE.Vector3(2, 1, 0)), 0xff5030);
              explode(at.clone().add(new THREE.Vector3(-2, -1, 0)), 0xffd050);
              spawnPickup(at.clone().add(new THREE.Vector3(1.5, 0, 0)));
              spawnPickup(at.clone().add(new THREE.Vector3(-1.5, 0, 0)));
              scene.remove(S.boss);
              S.boss = null;
              setBossState(null);
              S.score += 500; // boss bonus
              setScore(S.score);
            }
            continue;
          }
          for (const e of [...S.enemies]) {
            if (b.position.distanceToSquared(e.position) < e.userData.hitR2) {
              explode(e.position, 0xffa02a);
              if (Math.random() < 0.25) spawnPickup(e.position.clone()); // comeback drops
              removeFrom(S.enemies, e);
              removeFrom(S.playerBolts, b);
              S.score += 100;
              setScore(S.score);
              if (!mutedRef.current) sfxRef.current.boom();
              break;
            }
          }
        }

        // drifting power-ups (fly into them to collect)
        for (const p of [...S.pickups]) {
          p.position.z += 30 * dt;
          p.position.y = p.userData.baseY + Math.sin(S.t * 3 + p.position.x) * 0.5;
          p.rotation.x += dt * 2.4;
          p.rotation.y += dt * 3.1;
          if (p.position.z > 18) { removeFrom(S.pickups, p); continue; }
          if (p.position.distanceToSquared(player.position) < 8.5) collectPickup(p);
        }

        // enemy bolts vs player
        for (const b of [...S.enemyBolts]) {
          b.position.addScaledVector(b.userData.vel, dt);
          if (b.position.z > 20) { removeFrom(S.enemyBolts, b); continue; }
          if (b.position.distanceToSquared(player.position) < 3.2) {
            explode(player.position.clone(), 0xff4433);
            removeFrom(S.enemyBolts, b);
            playerHit();
          }
        }
      }

      // explosions decay
      for (const g of [...S.booms]) {
        g.userData.life -= dt;
        g.children.forEach((p) => {
          p.position.addScaledVector(p.userData.vel, dt);
          p.material.opacity = Math.max(0, g.userData.life / 0.65);
        });
        if (g.userData.life <= 0) removeFrom(S.booms, g);
      }

      // hit flash
      if (S.flash > 0) {
        S.flash = Math.max(0, S.flash - dt * 2.5);
        if (S.flashEl) S.flashEl.style.opacity = String(S.flash * 0.45);
      }

      renderer.render(scene, camera);
    };
    animate();

    return () => {
      delete window.__SW_TEST;
      cancelAnimationFrame(S.raf);
      ro.disconnect();
      renderer.domElement.removeEventListener("mousemove", onMove);
      renderer.domElement.removeEventListener("mousedown", onDown);
      renderer.domElement.removeEventListener("contextmenu", onCtx);
      window.removeEventListener("mouseup", onUp);
      renderer.dispose();
      mount.removeChild(renderer.domElement);
      stateRef.current = {};
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = () => {
    stateRef.current.reset?.();
    setPhase("playing");
  };

  return (
    <div className="space-y-3" data-testid="starwar-game">
      <div className="relative w-full rounded-xl overflow-hidden border border-border/60 bg-black select-none" style={{ height: "min(66vh, 640px)", minHeight: 420, cursor: phase === "playing" ? "crosshair" : "default" }}>
        <div ref={mountRef} className="absolute inset-0" data-testid="starwar-canvas" />
        {/* red hit flash */}
        <div ref={(el) => { if (stateRef.current) stateRef.current.flashEl = el; }} className="absolute inset-0 bg-red-600 pointer-events-none" style={{ opacity: 0 }} />

        {/* HUD */}
        <div className="absolute top-0 inset-x-0 flex items-center justify-between px-4 py-2 pointer-events-none">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1" data-testid="starwar-lives">
              {Array.from({ length: LIVES }).map((_, i) => (
                <Heart key={i} className={`w-4 h-4 ${i < lives ? "text-rose-500 fill-rose-500" : "text-zinc-700"}`} />
              ))}
            </div>
            {shieldCt > 0 && (
              <div className="flex items-center gap-0.5" data-testid="starwar-shields">
                {Array.from({ length: shieldCt }).map((_, i) => (
                  <Shield key={i} className="w-4 h-4 text-cyan-400 fill-cyan-400/40" />
                ))}
              </div>
            )}
            {rapidLeft > 0 && (
              <span className="flex items-center gap-0.5 font-mono text-[11px] font-bold text-amber-300" data-testid="starwar-rapid">
                <Zap className="w-3.5 h-3.5 fill-amber-300" />{rapidLeft}s
              </span>
            )}
          </div>
          <p className="font-mono text-xl font-bold text-amber-300 drop-shadow-[0_0_6px_rgba(255,200,60,0.8)]" data-testid="starwar-score">
            {score.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] font-bold text-emerald-400" data-testid="starwar-power">PWR L{1 + Math.floor(Math.min(score, 1500) / 100)}{score >= 1500 ? " MAX" : ""}</span>
            <span className="font-mono text-[10px] text-zinc-400 flex items-center gap-1" data-testid="starwar-best"><Trophy className="w-3 h-3 text-amber-400" /> BEST {best.toLocaleString()}</span>
            <button onClick={() => setMuted((m) => !m)} className="pointer-events-auto text-zinc-400 hover:text-zinc-200" title={muted ? "Unmute" : "Mute"} data-testid="starwar-mute-btn">
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* boss health bar */}
        {bossState && (
          <div className="absolute top-10 left-1/2 -translate-x-1/2 w-64 pointer-events-none" data-testid="starwar-boss-bar">
            <p className="font-mono text-[10px] font-bold text-rose-400 text-center tracking-[0.35em]">BOSS{bossState.num >= 2 ? ` LV${bossState.num}` : ""}</p>
            <div className="h-2 rounded bg-zinc-900/80 overflow-hidden border border-rose-500/50">
              <div className="h-full bg-rose-500 transition-all" style={{ width: `${(bossState.hp / bossState.max) * 100}%` }} data-testid="starwar-boss-hp" />
            </div>
          </div>
        )}

        {/* start overlay */}
        {phase === "start" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/70" data-testid="starwar-start-overlay">
            <p className="font-mono text-4xl font-bold tracking-[0.3em] text-amber-300 drop-shadow-[0_0_14px_rgba(255,200,60,0.9)]">STAR WAR</p>
            <p className="font-mono text-xs text-zinc-300 text-center max-w-sm leading-relaxed">
              Move the mouse to steer your ship. Click (or hold) to fire.
              Watch for three enemy types: gray TIE fighters, fast cyan-ringed darts and big pink spiked raiders. Your ship upgrades every 100 points and gets a BIG power jump every 500 (extra spread shots, faster lasers) — power maxes out at 1,500 points; a BOSS ship arrives every 500 points — destroy it for a +500 bonus. Later bosses fire bolt fans and ring bursts and call in escorts!
              Some enemies drop power-ups: <span className="text-cyan-300">◆ shield</span> (absorbs a hit) and <span className="text-amber-300">▲ rapid fire</span> — fly into them! You have {LIVES} lives.
            </p>
            <Button onClick={start} className="btn-3d h-10 px-8 font-mono text-sm font-bold" data-testid="starwar-start-btn">
              <Rocket className="w-4 h-4 mr-2" /> Launch
            </Button>
          </div>
        )}

        {/* game over overlay */}
        {phase === "over" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/75" data-testid="starwar-gameover">
            <p className="font-mono text-3xl font-bold tracking-[0.2em] text-rose-400">GAME OVER</p>
            <p className="font-mono text-lg text-amber-300" data-testid="starwar-final-score">Final score: {score.toLocaleString()}</p>
            <p className="font-mono text-xs text-zinc-400">Best: {Math.max(best, score).toLocaleString()}</p>
            <Button onClick={start} className="btn-3d h-10 px-8 font-mono text-sm font-bold" data-testid="starwar-restart-btn">
              <Rocket className="w-4 h-4 mr-2" /> Restart
            </Button>
          </div>
        )}
      </div>
      <p className="font-mono text-[10px] text-muted-foreground">
        Mouse steers · click or hold to shoot · +100 per fighter, +500 per boss · ship upgrades every 100 points · {LIVES} hits and it's over · fly into <span className="text-cyan-500">shield</span> / <span className="text-amber-500">rapid-fire</span> drops. Best score is saved to the leaderboard.
      </p>
      <Leaderboard refreshKey={lbKey} />
    </div>
  );
}
